import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import {
  Trophy,
  Star,
  PlusCircle,
  CalendarDays,
  Medal,
  Award,
} from "lucide-react";
import { format } from "date-fns";
import { GoLinkExternal } from "react-icons/go";
import {
  PlatformPageShell,
  PlatformPageHeader,
  VerifyBanner,
  EmptyConnectState,
  StatTilesRow,
  PlatformInternalScoreCard,
  SectionCard,
  getPlatformMeta,
  buildContestPlatformBreakdown,
  connectProfilePath,
  formatNumber,
} from "./platformStatsShared";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Codechef = () => {
  const navigate = useNavigate();
  const { user, getLatestToken } = useAuth();
  const currentUser = user?.profile;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentUser?.id) return;

    setLoading(true);
    try {
      const token = await getLatestToken();
      if (!token) throw new Error("Missing auth token");
      const response = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/client_dashboard/codechef`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      setData(result.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const refreshUser = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/codechef/refresh_user/${currentUser?.id}`,
        {
          method: "POST",
        }
      );

      if (!res.ok) {
        throw new Error("Error in refreshing user");
      }

      fetchData();
    } catch (error) {
      console.error("Error in refreshing user", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4 animate-pulse">
        {/* Verification Banner Skeleton */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-700/20 to-slate-900/20 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gray-200"></div>
            <div>
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>

        {/* Refresh Button Skeleton */}
        <div className="flex flex-col gap-1 items-end w-full">
          <div className="h-10 w-32 bg-gray-200 rounded"></div>
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
        </div>

        {/* Main Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex flex-col gap-2">
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-12 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Profile Details Card Skeleton */}
        <div className="grid grid-cols-1 gap-6">
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <div className="h-6 w-32 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="h-8 w-8 rounded bg-gray-200"></div>
                    <div>
                      <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-8">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contest History Table Skeleton */}
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <div className="h-6 w-36 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-3 px-4">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                      </th>
                      <th className="text-center py-3 px-4">
                        <div className="h-4 w-32 bg-gray-200 rounded mx-auto"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4].map((i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="py-3 px-4">
                          <div className="h-5 w-48 bg-gray-200 rounded"></div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <PlatformPageShell>
        <EmptyConnectState
          platformName="CodeChef"
          onConnect={() => navigate(connectProfilePath())}
        />
      </PlatformPageShell>
    );
  }

  const meta = getPlatformMeta("codechef");
  const scoreBreakdown =
    data.scoreBreakdown || buildContestPlatformBreakdown(data, "codechef");

  return (
    <PlatformPageShell>
      {!data?.verified ? (
        <VerifyBanner onVerify={() => navigate(connectProfilePath())} />
      ) : null}

      <PlatformPageHeader
        title="CodeChef"
        subtitle={`${formatNumber(data?.totalProblemsSolved ?? 0)} problems solved · ${formatNumber(data?.contestsParticipated ?? 0)} contests`}
        username={data?.username}
        profileUrl={meta.profileUrl(data?.username)}
        lastSynced={data?.lastUpdated}
        refreshing={refreshing}
        onRefresh={refreshUser}
      />

      <StatTilesRow
        items={[
          {
            label: "Problems",
            value: data?.totalProblemsSolved ?? 0,
            hint: "solved",
            icon: Award,
            wrap: "border-sky-100 bg-linear-to-br from-sky-50 to-white",
            iconWrap: "bg-sky-100 text-sky-600",
          },
          {
            label: "Contests",
            value: data?.contestsParticipated ?? 0,
            hint: "participated",
            icon: CalendarDays,
            wrap: "border-amber-100 bg-linear-to-br from-amber-50 to-white",
            iconWrap: "bg-amber-100 text-amber-600",
          },
          {
            label: "Rating",
            value: data?.rating ?? 0,
            hint: "current",
            icon: Trophy,
            wrap: "border-zinc-200/80 bg-white",
            iconWrap: "bg-indigo-50 text-indigo-600",
          },
          {
            label: "Max rating",
            value: data?.maxRating ?? 0,
            hint: data?.star ? `${data.star}★` : "peak",
            icon: Star,
            wrap: "border-emerald-100 bg-linear-to-br from-emerald-50 to-white",
            iconWrap: "bg-emerald-100 text-emerald-600",
          },
        ]}
      />

      <PlatformInternalScoreCard slug="codechef" scoreBreakdown={scoreBreakdown} />

      <SectionCard title="Profile details">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg">
                <Trophy className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-600">Current Rating</p>
                  <p className="text-xl font-bold">{data?.rating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-indigo-50 p-4 rounded-lg">
                <Award className="h-8 w-8 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-600">Max Rating</p>
                  <p className="text-xl font-bold">{data?.maxRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-yellow-50 p-4 rounded-lg">
                <Star className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-600">Star Rating</p>
                  <p className="text-xl font-bold">{data?.star}★</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-gray-600 mb-1">Username</p>
                <p className="font-semibold flex items-center gap-2">
                  <span>{data?.username}</span>
                  {data?.username && (
                    <Link
                      className="group"
                      target="_blank"
                      to={`https://www.codechef.com/users/${data?.username}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GoLinkExternal className="w-6 h-6 text-gray-400 group-hover:text-gray-700" />
                    </Link>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Full Name</p>
                <p className="font-semibold">{data?.fullName}</p>
              </div>
            </div>
      </SectionCard>

      <SectionCard title="Contest history">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-sm text-gray-600">
                    <th className="text-left py-3 px-4">Contest</th>
                    <th className="text-center py-3 px-4">Problems Solved</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.contestParticipation?.map((contest) => (
                    <tr
                      key={contest.id}
                      className="border-b last:border-b-0 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">
                            {contest.codechef_contest.name}
                          </p>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        {contest.problems_solved}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      </SectionCard>
    </PlatformPageShell>
  );
};

export default Codechef;
